import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { DestroyBase } from '../../../components/common/ng-destroy-base';
import { AppServiceHelperService } from '../../../services/app-service-helper.service';
import { EventBusService } from '../../../services/event-bus.service';
import { AppStorageService } from '../../../services/app-storage.service';
import { ReportDesignerWidgetLockInfo } from '../models/report-designer-widget-lock-info';
import { ObjectUtil } from '../../../components/common/object-util/object-util';
import { ReportDesignerWidgetConfig } from '../models/report-designer-widget-config';

/**
 * This service coordinates checking out Report Designer widgets for exclusive editing by a specific user.
 */
@Injectable()
export class WidgetLockerService extends DestroyBase {
  /**
   * Indicates whether the data is already loaded
   */
  private loaded = false;

  /**
   * Prevents from double-loading when called while loading
   */
  private loadStartedDfd: Subject<ReportDesignerWidgetLockInfo[]> = null;

  /**
   * Loaded report OID
   */
  private loadedReportId: number = null;

  /**
   * Widget lock data
   */
  private widgetLockData: ReportDesignerWidgetLockInfo[] = [];

  /**
   * Constructor
   * @param appStorage
   * @param eventBus
   * @param http
   * @param appServiceHelper
   */
  constructor(private appStorage: AppStorageService,
    private eventBus: EventBusService,
    private http: HttpClient,
    private appServiceHelper: AppServiceHelperService) {
    super();

    this.eventBus.subscribeEvent('saveChanges', () => {
      this.reload();
    })
  }

  /**
   * Loads the widget lock data for the specified report
   * @param reportId The OID of the report
   * @returns
   */
  public load(reportId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (this.loaded && this.loadedReportId == reportId) {
        //Already loaded - return immediately
        resolve();
      } else if (this.loadStartedDfd) {
        //Loading is already in progress - wait for it to terminate and return
        this.loadStartedDfd.pipe(take(1)).subscribe(() => {
          //Loading is done - resolve
          resolve();
        });
      }
      else {
        this.widgetLockData = [];
        this.loadedReportId = reportId;

        const url = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', `widgetlocker?reportId=${reportId}`);

        this.loadStartedDfd = new Subject<any>(); //Loading is in progress

        this.http.get(url).pipe(take(1)).subscribe({
          next: (data: any) => {
            this.widgetLockData = data;
            this.loaded = true;
            this.eventBus.broadcastEvent('report.designer.widget.locks.changed');
            this.loadStartedDfd.next(data);
            resolve();
          },
          error: (error) => {
            this.loadedReportId = null;
            reject(error);
          }
        });
      }
    });
  }

  /**
   * Reloads the widget lock data for the currently initialized report
   * @returns
   */
  public reload(): Promise<void> {
    this.loaded = false;
    if (this.loadStartedDfd) {
      this.loadStartedDfd.complete();
    }
    this.loadStartedDfd = null;
    this.widgetLockData = [];
    return this.load(this.loadedReportId);
  }

  /**
   * Tries to lock a widget and refreshes all lock data
   * @param reportId The report OID
   * @param widgetId The widget ID (Guid)
   * @param widgetTypeId The widget type id
   * @param multipleInstances Set this to true if the widget is of a type that allows multiple instances of the same widget class (for example text or picture widgets)
   * @param currentLayout The current widget layout
   * @param widgetConfig The widget configuration
   * @returns
   */
  public lockWidget(reportId, widgetId, widgetTypeId, multipleInstances, currentLayout, widgetConfig): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (reportId) {
        this.lockWidgetReportMode({ resolve, reportId, widgetId, widgetTypeId, multipleInstances, currentLayout, widgetConfig, reject });
      }
      else {
        this.lockWidgetTemplateMode(widgetConfig, resolve);
      }
    });
  }

  /**
   * Lock a widget in report mode
   * @param config
   */
  private lockWidgetReportMode(config: { reportId: any, widgetId: any, widgetTypeId: any, multipleInstances: any, currentLayout: any, widgetConfig: any, resolve: (value: void | PromiseLike<void>) => void, reject: (reason?: any) => void }) {
    const widgetsCheckedOutByUser = this.getLockedWidgetsForUser();
    if (widgetsCheckedOutByUser?.length >= 5) {
      this.warnTooManyWidgetsCheckedOut(config.resolve);
    }
    else {
      this.continueInnerLockWidgetReportMode(config);
    }
  }

  /**
   * Continue checking out (locking) a widget in report mode
   * @param config
   */
  private continueInnerLockWidgetReportMode(config: { reportId: any, widgetId: any, widgetTypeId: any, multipleInstances: any, currentLayout: any, widgetConfig: any, resolve: (value: void | PromiseLike<void>) => void, reject: (reason?: any) => void }) {
    const command = {
      reportId: config.reportId,
      widgetId: config.widgetId,
      widgetTypeId: config.widgetTypeId
    };
    if (config.multipleInstances) {
      command.widgetId += "::" + config.currentLayout;
    }

    const url = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', '/widgetlocker/lock');

    this.http.post(url, command).pipe(take(1)).subscribe({
      next: (result: any) => {
        this.widgetLockData = result.lockedWidgets;
        this.eventBus.broadcastEvent('report.designer.widget.locks.changed');

        const lockInfoForWidget = this.getLockInfoForWidget(config.widgetConfig);
        config.widgetConfig.widgetLockInfo = lockInfoForWidget;

        config.resolve();
      },
      error: (error) => {
        this.loadedReportId = null;
        config.reject(error);
      }
    });
  }

  /**
   * Too many widgets checked out: Show a warning
   * @param resolve
   */
  private warnTooManyWidgetsCheckedOut(resolve: (value: void | PromiseLike<void>) => void) {
    this.eventBus.broadcastEvent('openConfirmDialog', {
      success: () => {
        this.eventBus.broadcastEvent('report.designer.widget.locks.changed');
        resolve();
      }
    }, 'reportCheckOutMaximum5Widgets');
  }

  /**
   * Lock a widget in template mode
   * @param widgetConfig
   * @param resolve
   */
  private lockWidgetTemplateMode(widgetConfig: any, resolve: (value: void | PromiseLike<void>) => void) {
    let widgetId = widgetConfig.widgetId.toString();
    if (widgetConfig.widgetId > 0) {
      if (widgetConfig.multipleInstances) {
        widgetId += "::" + widgetConfig.widgetSettings.currentLayout;
      }
      const existingEntry = this.widgetLockData.find((element) => {
        return element.widgetId === widgetId;
      });
      const user = this.appStorage.getCurrentUserDetails();

      if (!existingEntry) {
        this.widgetLockData.push({
          locked: true,
          lockedBy: user.OID,
          lockedByCurrentUser: true,
          lockedByUserName: user.User_Description,
          lockedAt: new Date(),
          lockedByUserDescription: user.User_Description,
          lockedByUserId: user.OID,
          lockTime: '',
          widgetId: widgetId
        });
      }
      this.eventBus.broadcastEvent('report.designer.widget.locks.changed');
    }

    resolve();
  }

  /**
   * Unlocks a widget and refreshes all lock data. It can only be unlocked if it is currently locked by the active user or if the active user is an admin.
   * @param reportId The report OID
   * @param widgetId The widget ID (Guid)
   * @param widgetTypeId The widget type id
   * @param multipleInstances Set this to true if the widget is of a type that allows multiple instances of the same widget class (for example text or picture widgets)
   * @param currentLayout The current widget layout
   * @returns
   */
  public unlockWidget(reportId, widgetId, widgetTypeId, multipleInstances, currentLayout): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (reportId) {
        this.unlockWidgetReportMode(reportId, widgetId, widgetTypeId, multipleInstances, currentLayout, resolve, reject);
      }
      else {
        this.unlockWidgetTemplateMode(widgetId, multipleInstances, currentLayout, resolve);
      }
    });
  }

  /**
   * Unlock a widget in template mode
   * @param widgetId
   * @param multipleInstances
   * @param currentLayout
   * @param resolve
   */
  private unlockWidgetTemplateMode(widgetId: any, multipleInstances: any, currentLayout: any, resolve: (value: void | PromiseLike<void>) => void) {
    let widgetIdCompare = String(widgetId);
    if (multipleInstances) {
      widgetIdCompare += "::" + currentLayout;
    }

    this.widgetLockData = this.widgetLockData.filter((element) => {
      return element.widgetId !== widgetIdCompare;
    });

    this.eventBus.broadcastEvent('report.designer.widget.locks.changed');
    resolve();
  }

  /**
   * Unlock a widget in report mode
   * @param reportId
   * @param widgetId
   * @param widgetTypeId
   * @param multipleInstances
   * @param currentLayout
   * @param resolve
   * @param reject
   */
  private unlockWidgetReportMode(reportId: any, widgetId: any, widgetTypeId: any, multipleInstances: any, currentLayout: any, resolve: (value: void | PromiseLike<void>) => void, reject: (reason?: any) => void) {
    const command = {
      reportId: reportId,
      widgetId: widgetId,
      widgetTypeId: widgetTypeId
    };
    if (multipleInstances) {
      command.widgetId += "::" + currentLayout;
    }
    const url = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', '/widgetlocker/unlock');

    this.http.post(url, command).pipe(take(1)).subscribe({
      next: (result: any) => {
        this.widgetLockData = result.lockedWidgets;
        this.eventBus.broadcastEvent('report.designer.widget.locks.changed');
        resolve(result);
      },
      error: (error) => {
        this.loadedReportId = null;
        reject(error);
      }
    });
  }

  /**
   * Gets the current lock information for the specified widget
   * @param widgetConfig The widget configuration
   * @returns The lock info for the specified widget
   */
  public getLockInfoForWidget(widgetConfig: ReportDesignerWidgetConfig): ReportDesignerWidgetLockInfo {
    let widgetId = widgetConfig.widgetId.toString();
    if (widgetConfig.widgetId > -1000 && widgetConfig.widgetId < 0) {
      return {
        locked: true,
        lockedBy: 0,
        lockedByCurrentUser: true,
        lockedByUserName: '',
        lockedAt: null,
        lockedByUserDescription: '',
        lockedByUserId: 0,
        lockTime: '',
        widgetId: widgetId
      };
    }

    try {
      if (widgetConfig.multipleInstances) {
        widgetId += "::" + widgetConfig.widgetSettings.currentLayout;
      }

      if (this.widgetLockData?.length) {
        const lockData = this.widgetLockData.find((element) => {
          return element.widgetId === widgetId;
        });
        if (lockData) {
          const ret = ObjectUtil.deepCopy(lockData) as ReportDesignerWidgetLockInfo;
          ret.locked = true;
          ret.lockedBy = lockData.lockedByUserId;
          ret.lockedByCurrentUser = this.appStorage.getCurrentUserDetails().OID == lockData.lockedByUserId;
          ret.lockedByUserName = lockData.lockedByUserDescription;
          ret.lockedAt = lockData.lockTime;
          return ret;
        }
      }
    } catch (e) {
      //Nothing
    }
    return {
      widgetId: widgetId,
      locked: false,
      lockedBy: 0,
      lockedByCurrentUser: false,
      lockedByUserName: '',
      lockedAt: null,
      lockedByUserDescription: '',
      lockedByUserId: 0,
      lockTime: ''
    };
  }

  /**
   * Gets all widget inside the report that are locked by the current user
   * @returns
   */
  public getLockedWidgetsForUser(): ReportDesignerWidgetLockInfo[] {
    const userId = this.appStorage.getCurrentUserDetails().OID;
    return this.widgetLockData?.filter((element) => {
      return element.lockedByUserId === userId;
    }) || [];
  }
}
